import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import BlockFenceNodeView, { type BlockFenceNodeViewOptions, type RawFenceReason } from '../block-fence-node-view';
import { type BlockFenceRegistry, isBlockFenceKind } from '../block-fence-registry';

type MarkdownToken = {
  type: string;
  level: number;
  map: [number, number] | null;
  meta: Record<string, unknown> | null;
  content: string;
  info: string;
};

type MarkdownRenderer = {
  rules: Record<string, MarkdownRendererRule | undefined>;
  renderToken: MarkdownRendererRule;
};

type MarkdownRendererArgs = [
  tokens: MarkdownToken[],
  index: number,
  options: unknown,
  env: unknown,
  renderer: MarkdownRenderer,
];

type MarkdownRendererRule = (...args: MarkdownRendererArgs) => string;

type MarkdownIt = {
  renderer: MarkdownRenderer;
  core: {
    ruler: {
      after: (after: string, name: string, rule: (state: { src: string; tokens: MarkdownToken[] }) => void) => void;
    };
  };
};

type MarkdownSerializerState = {
  closeBlock: (node: unknown) => void;
  ensureNewLine: () => void;
  text: (text: string, escapeText?: boolean) => void;
  write: (text: string) => void;
};

const decodeConfig = (value: string | null) => {
  if (!value) return '{}';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// Interactive elements inside a block handle their own DOM events; keep those from reaching
// ProseMirror, which would treat them as edits to the atom node. The header bar is on the list for the
// opposite reason: the node view selects the block on a press there, and ProseMirror handling the same
// press would replace that selection with a caret beside the block. Drag/drop are the exception — they
// must reach ProseMirror for the drag handle to start a native node drag and for a drop to move it.
// This predicate replaces tiptap's default stopEvent, which had that carve-out built in.
export const shouldStopEvent = (event: Event): boolean => {
  if (event.type.startsWith('drag') || event.type === 'drop') return false;
  const target = event.target as HTMLElement | null;
  return !!target?.closest(
    '[contenteditable="true"], button, [role="textbox"], [data-inline-interactive], [data-resize-handle], [data-block-header]',
  );
};

const CLOSING_FENCE = /^[ ]{0,3}(`{3,}|~{3,})[ \t]*$/;

const RAW_FENCE_REASONS: readonly RawFenceReason[] = [
  'unclosed',
  'indented',
  'extraWords',
  'unknownKind',
  'invalidConfig',
];

const toRawFenceReason = (value: string | null): RawFenceReason | null =>
  RAW_FENCE_REASONS.find(reason => reason === value) ?? null;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Longer than any backtick run in the config, so no line of it can close the fence early.
const fenceFor = (config: string) => {
  const longest = Math.max(0, ...(config.match(/`+/g) ?? []).map(run => run.length));
  return '`'.repeat(Math.max(3, longest + 1));
};

type SerializedParent = {
  childCount: number;
  child: (index: number) => { type: { name: string }; textContent: string };
};

const hasContentAfter = (parent: SerializedParent, index: number) => {
  for (let next = index + 1; next < parent.childCount; next += 1) {
    const sibling = parent.child(next);
    if (sibling.type.name !== 'paragraph' || sibling.textContent.trim()) return true;
  }
  return false;
};

export type BlockFenceVocabulary = {
  // Tiptap node name. Two nodes claiming the same name or the same parseHTML tag in one editor
  // resolve nondeterministically, so each vocabulary owns all three of these.
  nodeName: string;
  // Fence info-string prefix, e.g. `footprint:` — the kind follows it.
  fencePrefix: string;
  // Attribute stem, e.g. `data-artifact-block`, giving `-kind` and `-config`.
  dataPrefix: string;
  registry: BlockFenceRegistry;
  prettyPrintConfig: boolean;
  // Every fence in the namespace (the prefix without its colon) is claimed, and one the runtime would
  // not compile is kept verbatim and shown as text, with RawFenceNotice saying why.
  requireTopLevelFence?: boolean;
  Wrapper?: BlockFenceNodeViewOptions['Wrapper'];
  RawFenceNotice?: BlockFenceNodeViewOptions['RawFenceNotice'];
  upgrade?: BlockFenceNodeViewOptions['upgrade'];
};

export const createBlockFenceExtension = (vocabulary: BlockFenceVocabulary) => {
  const { nodeName, fencePrefix, dataPrefix, registry } = vocabulary;
  const kindAttribute = `${dataPrefix}-kind`;
  const configAttribute = `${dataPrefix}-config`;
  const rawFenceAttribute = `${dataPrefix}-raw-fence`;
  const rawReasonAttribute = `${dataPrefix}-raw-reason`;
  const namespace = fencePrefix.replace(/:$/, '');
  const claimsInfo = (info: string) => new RegExp(`^${escapeRegExp(namespace)}(?![\\w])`).test(info);
  // Written over the prefix of an unclosed fence that save has to close, so closing it can't make it run.
  const unclosedPrefix = `${namespace}-unclosed:`;

  // An unclosed fence runs to the end of the document, so writing one back verbatim ahead of other
  // content would swallow that content on the next load. Only then is it closed, where the editor
  // shows it ending, and renamed out of the runnable namespace so the runtime still skips it.
  const closeRawFence = (raw: string): string => {
    const lines = raw.split('\n');
    const opening = /^([ ]{0,3}(`{3,}|~{3,})[ \t]*)(.*)$/.exec(lines[0] ?? '');
    if (!opening) return raw;
    const [, lead, marker, info] = opening;
    const closing = lines.length > 1 ? CLOSING_FENCE.exec(lines[lines.length - 1]) : null;
    if (closing && closing[1][0] === marker[0] && closing[1].length >= marker.length) return raw;
    const renamed = info.startsWith(fencePrefix) ? `${unclosedPrefix}${info.slice(fencePrefix.length)}` : info;
    const body = [`${lead}${renamed}`, ...lines.slice(1)].join('\n').replace(/\n*$/, '');
    return `${body}\n${marker}`;
  };

  const rawReasonFor = (token: MarkdownToken, info: string): RawFenceReason | null => {
    const placement = token.meta?.[`${nodeName}:top-level-fence`] as
      | { closed: boolean; columnZero: boolean }
      | undefined;
    if (!placement?.closed) return 'unclosed';
    if (!placement.columnZero) return 'indented';
    if (info.startsWith(unclosedPrefix)) return 'unclosed';
    if (!info.startsWith(fencePrefix)) return 'unknownKind';
    const [kind = '', ...extraWords] = info.slice(fencePrefix.length).split(/\s+/);
    if (!isBlockFenceKind(registry, kind)) return 'unknownKind';
    // A code block keeps only the first word, so this fence would come back from one edit runnable.
    if (extraWords.length > 0) return 'extraWords';
    // Saved from its parsed config, a fence that doesn't parse would lose its original delimiters.
    if (registry[kind].parse(token.content.trim()) === null) return 'invalidConfig';
    return null;
  };
  // Per-vocabulary, not per-module: each created extension patches a given renderer exactly once,
  // capturing whatever rule was there before. That is what lets two vocabularies chain — a shared
  // WeakSet would make the second one skip its patch and never render at all.
  const patchedRenderers = new WeakSet<object>();

  const renderClaimedFence = (token: MarkdownToken, info: string, source: string) => {
    const reason = rawReasonFor(token, info);
    const kind = info.slice(fencePrefix.length).split(/\s+/)[0] ?? '';
    const knownKind = info.startsWith(fencePrefix) && isBlockFenceKind(registry, kind) ? kind : '';
    if (reason) {
      return `<div ${kindAttribute}="${knownKind}" ${rawFenceAttribute}="${encodeURIComponent(source)}" ${rawReasonAttribute}="${reason}"></div>`;
    }
    return `<div ${kindAttribute}="${knownKind}" ${configAttribute}="${encodeURIComponent(token.content.trim())}"></div>`;
  };

  return Node.create({
    name: nodeName,
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addOptions() {
      return vocabulary;
    },

    addAttributes() {
      return {
        // A block only ever comes from a parsed fence, which always names its kind. An unnamed one
        // renders the invalid-block fallback rather than guessing.
        kind: { default: '' },
        config: { default: '{}' },
        ...(vocabulary.requireTopLevelFence && { rawFence: { default: null }, rawReason: { default: null } }),
      };
    },

    parseHTML() {
      return [
        {
          tag: `div[${kindAttribute}]`,
          getAttrs: element => {
            if (!(element instanceof HTMLElement)) return false;
            const kind = element.getAttribute(kindAttribute) ?? '';
            if (vocabulary.requireTopLevelFence && element.hasAttribute(rawFenceAttribute)) {
              return {
                kind: isBlockFenceKind(registry, kind) ? kind : '',
                rawFence: decodeConfig(element.getAttribute(rawFenceAttribute)),
                rawReason: toRawFenceReason(element.getAttribute(rawReasonAttribute)),
              };
            }
            if (!kind || !isBlockFenceKind(registry, kind)) return false;
            return { kind, config: decodeConfig(element.getAttribute(configAttribute)) };
          },
        },
      ];
    },

    renderHTML({ node }) {
      return [
        'div',
        {
          [kindAttribute]: node.attrs.kind,
          [configAttribute]: encodeURIComponent(node.attrs.config as string),
          ...(node.attrs.rawFence && {
            [rawFenceAttribute]: encodeURIComponent(node.attrs.rawFence as string),
            [rawReasonAttribute]: node.attrs.rawReason,
          }),
        },
      ];
    },

    addProseMirrorPlugins() {
      if (!vocabulary.requireTopLevelFence) return [];
      // The runtime rejects a fence that isn't at column one, so a block pasted or dropped into a list
      // item, quote or table cell would look executable here and fail to publish. The slash command
      // already inserts at the top level; this catches every other way in.
      return [
        new Plugin({
          key: new PluginKey(`${nodeName}:top-level`),
          appendTransaction: (transactions, _oldState, newState) => {
            if (!transactions.some(transaction => transaction.docChanged)) return null;
            const nested: number[] = [];
            newState.doc.descendants((node, pos, parent) => {
              if (node.type.name === nodeName && parent !== newState.doc) nested.push(pos);
              return true;
            });
            if (nested.length === 0) return null;
            const { tr } = newState;
            for (const pos of nested.reverse()) {
              const $pos = tr.doc.resolve(pos);
              const node = tr.doc.nodeAt(pos);
              if (!node) continue;
              const after = $pos.after(1);
              tr.insert(after, node);
              tr.deleteRange(pos, pos + node.nodeSize);
            }
            return tr;
          },
        }),
      ];
    },

    addNodeView() {
      return ReactNodeViewRenderer(BlockFenceNodeView, {
        stopEvent: ({ event }) => shouldStopEvent(event),
      });
    },

    addStorage() {
      return {
        markdown: {
          serialize(
            state: MarkdownSerializerState,
            node: { attrs: { config: string; kind: string; rawFence?: string } },
            parent?: SerializedParent,
            index?: number,
          ) {
            if (node.attrs.rawFence) {
              const followed = !!parent && index !== undefined && hasContentAfter(parent, index);
              state.write(followed ? closeRawFence(node.attrs.rawFence) : node.attrs.rawFence);
              state.closeBlock(node);
              return;
            }
            const fence = fenceFor(node.attrs.config);
            state.write(`${fence}${fencePrefix}${node.attrs.kind}\n`);
            state.text(node.attrs.config, false);
            state.ensureNewLine();
            state.write(fence);
            state.closeBlock(node);
          },
          parse: {
            setup(markdown: MarkdownIt) {
              const { renderer } = markdown;
              if (patchedRenderers.has(renderer)) return;
              patchedRenderers.add(renderer);

              const placementKey = `${nodeName}:top-level-fence`;
              const sourceKey = `${placementKey}:source`;
              if (vocabulary.requireTopLevelFence) {
                markdown.core.ruler.after('block', placementKey, state => {
                  const lines = state.src.split('\n');
                  for (const token of state.tokens) {
                    if (token.type !== 'fence' || !claimsInfo(token.info.trim())) continue;
                    const opening = token.map && /^[ ]{0,3}(`{3,}|~{3,})/.exec(lines[token.map[0]] ?? '');
                    const closing = token.map && CLOSING_FENCE.exec(lines[token.map[1] - 1] ?? '');
                    token.meta = {
                      ...token.meta,
                      [placementKey]: {
                        closed: !!(
                          opening &&
                          closing &&
                          token.map &&
                          token.map[1] - token.map[0] > 1 &&
                          opening[1][0] === closing[1][0] &&
                          closing[1].length >= opening[1].length
                        ),
                        columnZero: !!token.map && /^(`{3,}|~{3,})/.test(lines[token.map[0]] ?? ''),
                      },
                      [sourceKey]: token.map ? lines.slice(token.map[0], token.map[1]).join('\n') : null,
                    };
                  }
                });
              }

              const renderPreviousFence = renderer.rules.fence;
              const renderFence: MarkdownRendererRule = (...args) => {
                const [tokens, index, options, env, self] = args;
                const token = tokens[index];
                const info = token.info.trim();
                const source = token.meta?.[sourceKey];
                if (vocabulary.requireTopLevelFence && token.level === 0 && typeof source === 'string') {
                  return renderClaimedFence(token, info, source);
                }
                const kind = info.startsWith(fencePrefix) ? info.slice(fencePrefix.length) : '';
                if (vocabulary.requireTopLevelFence || !kind || !isBlockFenceKind(registry, kind)) {
                  return (
                    renderPreviousFence?.(tokens, index, options, env, self) ??
                    self.renderToken(tokens, index, options, env, self)
                  );
                }

                const config = encodeURIComponent(token.content.trim());
                return `<div ${kindAttribute}="${kind}" ${configAttribute}="${config}"></div>`;
              };
              renderer.rules.fence = renderFence;
            },
          },
        },
      };
    },
  });
};
